import React from "react";
import axios from "axios";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ImageLightbox from "../../Components/ImageLightbox";
import OrderDetailsModal, {buildRows} from "../../Components/OrderDetailsModal";
import ZoomableReceipt from "./ZoomableReceipt";

const baseUrl = "https://api.appetito.ro/";

// Comenzi > "Diferențe preț" (super admins only): orders a courier marked
// at pickup as having a different total than the restaurant's receipt. The
// admin enters the receipt total (products only: delivery and our fees
// aren't on it) and the difference is added to the order as a "Diferență
// preț" line; for card payments contact@appetito.ro gets an email to charge
// or refund the customer. Either way the order leaves this list.

const STATUS_LABELS = {
  created: "Creată",
  accepted: "Acceptată",
  prepared: "Preparată",
  picked: "Ridicată",
  delivered: "Livrată",
  canceled: "Anulată",
};

const authHeaders = () => ({headers: {Authorization: `Bearer ${localStorage.getItem("access_token")}`}});

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

const money = (value) => `${(Number(value) || 0).toFixed(2)} lei`;

const parseAmount = (text) => {
  const value = parseFloat(String(text).replace(",", "."));
  return isNaN(value) ? null : value;
};

function pad(n) {
  return `${n}`.padStart(2, "0");
}

function formatDate(value) {
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return "";
  }
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default class PriceDifferences extends React.Component {
  state = {
    loading: true,
    error: "",
    orders: [],
    since: "",
    correcting: null, // the order being corrected / marked fixed
    mode: "correct", // or "fixed"
    details: null, // its products (GET v1/admin/order/:id)
    receiptTotal: "",
    description: "",
    saving: false,
    formError: "",
    notice: "",
    receipt: null,
    selectedOrder: null,
  };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    this.setState({loading: true, error: ""});
    try {
      const response = await axios.get(`${baseUrl}v1/admin/price-differences`, authHeaders());
      this.setState({loading: false, orders: response.data.orders || [], since: response.data.since});
      if (this.props.onCount) {
        this.props.onCount((response.data.orders || []).length);
      }
    } catch (e) {
      this.setState({loading: false, error: errorMessage(e, "Lista nu a putut fi încărcată.")});
    }
  };

  open = async (order, mode) => {
    this.setState({correcting: order, mode, details: null, receiptTotal: "", description: "", formError: ""});
    try {
      const response = await axios.get(`${baseUrl}v1/admin/order/${order.order_id}`, authHeaders());
      if (this.state.correcting?.order_id === order.order_id) {
        this.setState({details: response.data});
      }
    } catch (e) {
      this.setState({formError: "Produsele comenzii nu au putut fi încărcate."});
    }
  };

  close = () => this.setState({correcting: null});

  submit = async () => {
    const {correcting: order, mode, receiptTotal, description} = this.state;
    this.setState({saving: true, formError: ""});
    try {
      if (mode === "correct") {
        const response = await axios.post(`${baseUrl}v1/admin/price-differences/${order.order_id}/correct`, {
          receipt_total: parseAmount(receiptTotal),
          description,
        }, authHeaders());
        const {difference, email_sent: emailSent, email_error: emailError} = response.data;
        let notice = `Comanda #${order.order_id}: s-a adăugat „Diferență preț” de ${difference > 0 ? "+" : ""}${money(difference)}.`;
        if (emailSent) {
          notice += " Email trimis la contact@appetito.ro.";
        }
        this.setState({notice: emailError ? `${notice} ${emailError}` : notice});
      } else {
        await axios.post(`${baseUrl}v1/admin/price-differences/${order.order_id}/fixed`, {description}, authHeaders());
        this.setState({notice: `Comanda #${order.order_id} a fost marcată ca rezolvată.`});
      }
      this.setState({saving: false, correcting: null});
      this.load();
    } catch (e) {
      this.setState({saving: false, formError: errorMessage(e, "Nu s-a putut salva.")});
    }
  };

  openOrder = async (orderId) => {
    try {
      const response = await axios.get(`${baseUrl}v1/admin/order/${orderId}`, authHeaders());
      this.setState({selectedOrder: response.data});
    } catch (e) {
      window.alert("Comanda nu a putut fi încărcată.");
    }
  };

  // Our order next to the restaurant's receipt: only what the receipt has
  // (products, extras, packaging), no delivery or our fees.
  renderOrderProducts() {
    const {details, correcting: order} = this.state;
    if (!details) {
      return <div className="price-diff-muted">Se încarcă produsele comenzii…</div>;
    }
    const rows = buildRows(details.products);
    const mentions = (details.products || []).filter((p) => p.mentions);
    return (
      <>
        <table className="price-diff-products">
          <thead>
          <tr>
            <th>Produs</th>
            <th>Cant.</th>
            <th>Preț</th>
            <th>Total</th>
          </tr>
          </thead>
          <tbody>
          {rows.map((row) => (
            <tr key={row.key} className={row.isExtra ? "price-diff-extra-row" : ""}>
              <td>{row.name}</td>
              <td>{row.quantity}</td>
              <td>{row.unitPrice.toFixed(2)}</td>
              <td>{row.total.toFixed(2)}</td>
            </tr>
          ))}
          </tbody>
          <tfoot>
          <tr>
            <td colSpan={3}>Total produse (fără transport și taxe)</td>
            <td>{money(order.products_total)}</td>
          </tr>
          </tfoot>
        </table>
        {mentions.length > 0 &&
        <div className="price-diff-muted price-diff-mentions">
          {mentions.map((p, i) => <div key={i}>{p.name}: {p.mentions}</div>)}
        </div>}
        {!!details.mentions && <div className="price-diff-muted price-diff-mentions">Mențiuni comandă: {details.mentions}</div>}
      </>
    );
  }

  renderForm() {
    const {correcting: order, mode, receiptTotal, description, saving, formError} = this.state;
    if (!order) {
      return null;
    }
    const amount = parseAmount(receiptTotal);
    const difference = amount === null ? null : Math.round((amount - order.products_total) * 100) / 100;
    const correct = mode === "correct";
    return (
      <Dialog open onClose={this.close} fullWidth maxWidth="xl">
        <DialogTitle disableTypography className="price-diff-title">
          <span>
            {correct ? "Corectează prețul" : "Marchează rezolvată"} · comanda #{order.order_id} ·{" "}
            {order.restaurant}{order.restaurant_city ? `, ${order.restaurant_city}` : ""} · {order.paid ? "Card" : "Cash"}
          </span>
          <IconButton onClick={this.close}><CloseIcon/></IconButton>
        </DialogTitle>
        <DialogContent dividers className="price-diff-compare">
          <div className="price-diff-compare-receipt">
            <div className="price-diff-column-title">Bonul restaurantului</div>
            <ZoomableReceipt src={order.receipt_url} alt={`Bon comandă #${order.order_id}`}/>
          </div>
          <div className="price-diff-compare-order">
            <div className="price-diff-column-title">Comanda noastră</div>
            {this.renderOrderProducts()}
            {correct &&
            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              label="Total pe bonul restaurantului (lei)"
              value={receiptTotal}
              onChange={(e) => this.setState({receiptTotal: e.target.value, formError: ""})}
              onKeyDown={(e) => e.key === "Enter" && amount !== null && description.trim() && !saving && this.submit()}
              inputProps={{inputMode: "decimal"}}
              helperText="Doar produsele de pe bon, fără transport și taxele noastre."
              margin="normal"/>}
            {correct && difference !== null &&
            <div className={`price-diff-result ${difference < 0 ? "price-diff-refund" : "price-diff-charge"}`}>
              Diferență: {difference > 0 ? "+" : ""}{money(difference)}
              {order.paid && difference !== 0 &&
              <div className="price-diff-result-note">
                Plată cu cardul: {difference > 0 ? "de încasat de la client" : "de returnat clientului"}, se trimite email la contact@appetito.ro.
              </div>}
            </div>}
            <TextField
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              label={correct ? "Descriere (apare pe comandă la „Diferență preț”)" : "Notă (opțional)"}
              placeholder={correct ? "ex. Pizza Diavola indisponibilă, înlocuită cu Quattro Formaggi după discuția cu clientul" : ""}
              value={description}
              onChange={(e) => this.setState({description: e.target.value, formError: ""})}
              inputProps={{maxLength: 1000}}
              margin="normal"/>
            {!!formError && <div className="price-diff-error">{formError}</div>}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.close} disabled={saving}>Renunță</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={this.submit}
            disabled={saving || (correct && (amount === null || !description.trim()))}>
            {saving ? "Se salvează…" : correct ? "Adaugă diferența" : "Marchează rezolvată"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const {loading, error, orders, since, notice, receipt, selectedOrder} = this.state;
    return (
      <Dialog open onClose={this.props.onClose} fullWidth maxWidth="lg" scroll="paper">
        <DialogTitle disableTypography className="price-diff-title">
          <span>Diferențe preț {!loading && `(${orders.length})`}</span>
          <IconButton onClick={this.props.onClose}><CloseIcon/></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <div className="price-diff-muted price-diff-intro">
            Comenzi marcate de livratori cu preț diferit față de bon{since ? `, din ${since.split("-").reverse().join(".")}` : ""}.
            Introdu totalul de pe bon (fără transport și taxele noastre) sau marchează comanda ca rezolvată.
          </div>
          {!!notice && <div className="price-diff-notice">{notice}</div>}
          {!!error && <div className="price-diff-error">{error}</div>}
          {loading && <div className="loader-container"><div className="loader"/></div>}
          {!loading && !error && orders.length === 0 &&
          <div className="price-diff-muted">Nicio comandă cu diferență de preț nerezolvată.</div>}
          {!loading && orders.length > 0 &&
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Id</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Restaurant</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Livrator</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Plată</TableCell>
                <TableCell align="right">Total produse</TableCell>
                <TableCell>Bon</TableCell>
                <TableCell/>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.order_id} hover>
                  <TableCell>
                    <span className="price-diff-link" onClick={() => this.openOrder(order.order_id)}>{order.order_id}</span>
                  </TableCell>
                  <TableCell className="price-diff-nowrap">{formatDate(order.created)}</TableCell>
                  <TableCell>{order.restaurant}{order.restaurant_city ? `, ${order.restaurant_city}` : ""}</TableCell>
                  <TableCell>
                    {order.client || "—"}
                    {!!order.phone && <div className="price-diff-muted">{order.phone}</div>}
                  </TableCell>
                  <TableCell>{order.deliverer || "—"}</TableCell>
                  <TableCell>{STATUS_LABELS[order.status] || order.status}</TableCell>
                  <TableCell>
                    <span className={order.paid ? "price-diff-card" : ""}>{order.paid ? "Card" : "Cash"}</span>
                  </TableCell>
                  <TableCell align="right" className="price-diff-nowrap">{money(order.products_total)}</TableCell>
                  <TableCell>
                    <img
                      src={order.receipt_url}
                      alt={`Bon #${order.order_id}`}
                      className="price-diff-thumb"
                      onClick={() => this.setState({receipt: order})}/>
                  </TableCell>
                  <TableCell className="price-diff-actions">
                    <Button size="small" variant="contained" color="primary" onClick={() => this.open(order, "correct")}>
                      Corectează prețul
                    </Button>
                    <Button size="small" onClick={() => this.open(order, "fixed")}>Rezolvată</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>}
        </DialogContent>
        {this.renderForm()}
        <ImageLightbox
          src={receipt ? receipt.receipt_url : null}
          alt={receipt ? `Bon comandă #${receipt.order_id}` : ""}
          onClose={() => this.setState({receipt: null})}/>
        <OrderDetailsModal order={selectedOrder} onClose={() => this.setState({selectedOrder: null})}/>
      </Dialog>
    );
  }
}
